import React from 'react';
import Animated from 'react-native-reanimated';
import MoreOptionsButton from 'src/components/10-atoms/dropdown-menu/MoreOptionsButton';
import VerticalThreeDotIcon from 'src/components/10-atoms/icons/VerticalThreeDotIcon';
import Row from 'src/components/10-atoms/layout/Row';
import RowCenter from 'src/components/10-atoms/layout/RowCenter';
import Typography from 'src/components/10-atoms/typography/Typography';
import { TypographyVariant } from 'src/customizations/themes/10-parts/typography';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import { renderNamedSlot } from 'src/utils/component';
import styleUtils from 'src/utils/styles';
import Navbar from './Navbar.template';
import PageNavbarIcon from './PageNavbarIcon.template';
import { getBackIcon } from './utils';
function isRenderAction(action) {
    return 'render' in action;
}
function navbarRenderProps(action) {
    return { title: action.title };
}
function toOverflowOption(action) {
    if (isRenderAction(action)) {
        const props = navbarRenderProps(action);
        return {
            title: action.title,
            render: () => renderNamedSlot(action.render, props),
        };
    }
    return action;
}
export const Actions = ({ actions, maxVisibleActions = 2 }) => {
    const [{ theme }] = useTheme();
    const renderAction = (action) => {
        if (isRenderAction(action)) {
            return (React.createElement(React.Fragment, { key: action.title }, renderNamedSlot(action.render, navbarRenderProps(action))));
        }
        const { Icon, onPress, to, title } = action;
        return (React.createElement(PageNavbarIcon, { key: title, Icon: Icon, onPress: onPress, to: to, color: theme.palette.icon.primary }));
    };
    const validActions = actions
        ? actions.filter(Boolean)
        : [];
    const shouldRender = validActions.length > 0;
    const moreOptions = validActions.slice(maxVisibleActions - 1);
    const shouldRenderMoreOptions = moreOptions.length > 1;
    const visibleActions = validActions.slice(0, shouldRenderMoreOptions ? maxVisibleActions - 1 : maxVisibleActions);
    return !shouldRender ? null : (React.createElement(Row, { yAxis: "center", xAxis: "flex-end", spacing: theme.spacing.standard.xs, pr: theme.spacing.standard.xs },
        visibleActions.map(renderAction),
        shouldRenderMoreOptions && (React.createElement(MoreOptionsButton, { renderButton: () => (React.createElement(RowCenter, { style: styleUtils.pressableArea },
                React.createElement(VerticalThreeDotIcon, null))), options: moreOptions.map(toOverflowOption) }))));
};
const PageNavbar = React.forwardRef(function PageNavbar({ title, actions, children, backType = 'none', onBackPress, maxVisibleActions, ...rest }, ref) {
    const [{ theme, deviceType }] = useTheme();
    maxVisibleActions = maxVisibleActions ?? (deviceType.isPhone ? 2 : 3);
    const renderBackIcon = () => {
        const Icon = getBackIcon(backType);
        if (Icon === null) {
            return null;
        }
        return React.createElement(PageNavbarIcon, { Icon: Icon, onPress: onBackPress });
    };
    return (React.createElement(Navbar, { ref: ref, ...rest },
        backType !== 'custom' && renderBackIcon(),
        React.createElement(Row, { flex: true, pl: backType === 'none' ? theme.spacing.page : 0 },
            title && (React.createElement(Typography, { variant: TypographyVariant.PAGE_TITLE, numberOfLines: 1, ellipsizeMode: "tail", accessibilityRole: "header", "aria-level": 1, flex: true }, title)),
            children),
        React.createElement(Actions, { actions: actions, maxVisibleActions: maxVisibleActions })));
});
export default PageNavbar;
export const AnimatedPageNavbar = Animated.createAnimatedComponent(PageNavbar);
